import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Link } from '@tanstack/react-router'
import EventPayIcon from '@/assets/EventPay-Icon.svg'

export const Route = createFileRoute('/auth/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState<'email' | 'verify'>('email')

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const result = await apiClient.sendPasswordResetCode(email)
      if (result.success) {
        setStep('verify')
        setSuccess('Reset code sent! Check your email.')
      } else {
        setError(result.error || 'Failed to send reset code')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const result = await apiClient.verifyPasswordResetCode(email, code, newPassword)
      if (result.success) {
        setSuccess('Password reset successful!')
        setTimeout(() => {
          navigate({ to: '/auth/login', search: { redirect: '/events', activated: undefined } })
        }, 2000)
      } else {
        setError(result.error || 'Failed to reset password')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sky background with clouds effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-300 via-sky-200 to-sky-100" />
      
      {/* Decorative circles/arcs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full border border-white/30" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-white/20" />
      </div>
      
      {/* Cloud-like shapes */}
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-white/80 to-transparent" />
      <div className="absolute bottom-20 left-10 w-64 h-32 bg-white/50 rounded-full blur-3xl" />
      <div className="absolute bottom-32 right-20 w-80 h-40 bg-white/40 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-1/3 w-72 h-36 bg-white/60 rounded-full blur-3xl" />
      
      {/* Card */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div 
          className="w-full max-w-md animate-fade-in-up"
          style={{
            animation: 'fadeInUp 0.6s ease-out forwards',
          }}
        >
          <div className="bg-gradient-to-b from-white/95 to-sky-50/90 backdrop-blur-xl rounded-3xl shadow-xl shadow-sky-200/50 border border-white/60 p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={EventPayIcon} 
                alt="EventPay" 
                className="w-16 h-16 rounded-2xl shadow-lg shadow-blue-500/30"
              />
            </div>
            
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                {step === 'email' ? 'Forgot password?' : 'Reset password'}
              </h1>
              <p className="text-neutral-500 text-sm leading-relaxed">
                {step === 'email'
                  ? "Enter your email and we'll send you a reset code"
                  : 'Enter the code from your email and your new password'}
              </p>
            </div>
            
            {step === 'email' ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50/80 rounded-xl border border-red-100">
                    {error}
                  </div>
                )}
                
                {/* Email field */}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="pl-10 h-12 rounded-xl bg-white/80 border-neutral-200/80 focus:bg-white transition-colors"
                  />
                </div>
                
                {/* Submit button */}
                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-medium text-base shadow-lg shadow-neutral-900/20 transition-all hover:shadow-xl hover:shadow-neutral-900/30" 
                  isLoading={isLoading}
                >
                  Send reset code
                </Button>
                
                {/* Back to login */}
                <Link
                  to="/auth/login"
                  search={{ redirect: '/events', activated: undefined }}
                  className="flex items-center justify-center gap-2 text-sm text-neutral-500 hover:text-neutral-700 transition-colors mt-4"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                  </svg>
                  Back to login
                </Link>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50/80 rounded-xl border border-red-100">
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="p-3 text-sm text-green-600 bg-green-50/80 rounded-xl border border-green-100">
                    {success}
                  </div>
                )}
                
                {/* Code field */}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33" />
                    </svg>
                  </div>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter code from email"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    className="pl-10 h-12 rounded-xl bg-white/80 border-neutral-200/80 focus:bg-white transition-colors"
                  />
                </div>
                
                {/* New password field */}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="pl-10 pr-10 h-12 rounded-xl bg-white/80 border-neutral-200/80 focus:bg-white transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    )}
                  </button>
                </div>
                
                {/* Submit button */}
                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-medium text-base shadow-lg shadow-neutral-900/20 transition-all hover:shadow-xl hover:shadow-neutral-900/30" 
                  isLoading={isLoading}
                >
                  Reset password
                </Button>
                
                {/* Back link */}
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="w-full text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  Didn't receive a code? Try again
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      
      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}
